import { useState } from 'react';
import { Button, Snackbar, Alert, Tooltip } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { userApi } from '../../services/userApi';
import { useAuthStore } from '../../stores/authStore';

interface FavoriteRetailerButtonProps {
  /** The Retailer id to favorite. */
  retailerId: string;
  /** Retailer name for accessible naming. */
  retailerName: string;
  /** Compact icon-only rendering for dense rows. */
  iconOnly?: boolean;
}

/**
 * Per-retailer "Favorite" toggle (issue #9 — favorite retailers). Mirrors the
 * WatchButton pattern: logged-out users are routed to /login with a return
 * path so they land back on the retailer page; logged-in users toggle the
 * retailer in their favorites via POST/DELETE /user/favorite-retailers.
 *
 * The component owns its own favorites query (keyed on the user's favorites
 * list) so the toggled state survives re-renders and reflects pre-existing
 * favorites on first load. Anonymous users skip the query entirely.
 */
export function FavoriteRetailerButton({
  retailerId,
  retailerName,
  iconOnly = false,
}: FavoriteRetailerButtonProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string } | null>(
    null
  );

  const favoritesQuery = useQuery({
    queryKey: ['favorite-retailers'],
    queryFn: () => userApi.listFavoriteRetailers(),
    enabled: isAuthenticated,
    retry: false,
  });

  const favoriteItem = favoritesQuery.data?.favorites.find((f) => f.retailerId === retailerId);
  const isFavorited = Boolean(favoriteItem);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['favorite-retailers'] });

  const addMutation = useMutation({
    mutationFn: () => userApi.addFavoriteRetailer(retailerId),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Added to your favorite retailers.' });
      void invalidate();
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t add to favorites: ${err.message}` });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => userApi.removeFavoriteRetailer(favoriteItem?.id as string),
    onSuccess: () => {
      setToast({ severity: 'success', message: 'Removed from your favorite retailers.' });
      void invalidate();
    },
    onError: (err: Error) => {
      setToast({ severity: 'error', message: `Couldn’t remove from favorites: ${err.message}` });
    },
  });

  const pending = addMutation.isPending || removeMutation.isPending;

  function onClick() {
    if (!isAuthenticated) {
      const returnPath = `${location.pathname}${location.search}`;
      navigate(`/login?return=${encodeURIComponent(returnPath)}`);
      return;
    }
    if (isFavorited) {
      removeMutation.mutate();
    } else {
      addMutation.mutate();
    }
  }

  const label = isFavorited
    ? `Remove ${retailerName} from favorite retailers`
    : `Add ${retailerName} to favorite retailers`;
  const text = isFavorited ? 'Favorited' : 'Favorite';
  const icon = isFavorited ? (
    <FavoriteIcon fontSize="small" />
  ) : (
    <FavoriteBorderIcon fontSize="small" />
  );

  return (
    <>
      <Tooltip title={isAuthenticated ? label : 'Sign in to favorite this retailer'}>
        <span>
          <Button
            size="small"
            variant={isFavorited ? 'contained' : 'outlined'}
            color={isFavorited ? 'primary' : 'inherit'}
            onClick={onClick}
            disabled={pending}
            aria-label={label}
            aria-pressed={isFavorited}
            startIcon={iconOnly ? undefined : icon}
            sx={{ whiteSpace: 'nowrap', minWidth: iconOnly ? 0 : undefined }}
          >
            {iconOnly ? icon : text}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default FavoriteRetailerButton;
